/**
 * Custom rewrite styles API client (spec §4.5, epic pirx-2gm7r).
 *
 * Thin wrappers over the `/api/styles` routes backing the settings styles
 * section. The shared `request()` helper attaches the active-team header
 * (see `services/api/request.ts`), so callers don't need to thread the team
 * id through — same pattern `teams-client.ts` follows. Team membership and
 * the `customRewriteStylesEnabled` entitlement are re-checked server-side.
 */

import { request, requestVoid } from "./request";

export interface StyleNormalizedStyleGuide {
  tone?: string;
  formalityLevel?: string;
  additionalRules?: string;
}

export interface Style {
  id: string;
  teamId: string;
  name: string;
  brandLabel: string | null;
  rawRules: string;
  normalizedStyleGuide: StyleNormalizedStyleGuide | null;
  createdAt: string;
  updatedAt: string;
}

export interface SaveStyleResult {
  style: Style;
  strippedRules: string[];
}

export type CreateStyleInput = {
  name: string;
  brandLabel?: string | null;
  rawRules: string;
};

export type UpdateStyleInput = {
  name?: string;
  brandLabel?: string | null;
  rawRules?: string;
};

/** List every style owned by the active team. */
export async function listStyles(): Promise<{ styles: Style[] }> {
  return request<{ styles: Style[] }>("/styles");
}

/** Create a style. The server runs the normalization/screening LLM call
 * and returns any rules it stripped as a short string array. */
export async function createStyle(
  input: CreateStyleInput,
): Promise<SaveStyleResult> {
  return request<SaveStyleResult>("/styles", {
    method: "POST",
    body: input,
    contentType: "json",
  });
}

/** Patch a style. The server re-runs the normalization call only when
 * `rawRules` actually changes (see `style.service.ts:update`). */
export async function updateStyle(
  styleId: string,
  input: UpdateStyleInput,
): Promise<SaveStyleResult> {
  return request<SaveStyleResult>(`/styles/${styleId}`, {
    method: "PATCH",
    body: input,
    contentType: "json",
  });
}

/** Delete a style. 204 on success; the server cascades dependent styled
 * variants outright rather than marking them stale (spec §4.1, Decision 4.1). */
export async function deleteStyle(styleId: string): Promise<void> {
  return requestVoid(`/styles/${styleId}`, {
    method: "DELETE",
  });
}
